import * as React from "react";
import { useHgsSettings } from "../../lib/HgsSettings/useHgsSettings";
import { currencyFormatter } from "../../lib/common";
import { ProductContext, ProductContextProvider } from "./product-context";
import { AttributeOption, Product, ProductCartModel } from "./types";
import { AttributeSelector } from "./AttributeSelector";

import "../../css/productDetail.scss";
import { AddToCartButton } from "./AddToCartButton";
import { QuantityEditor } from "./QuantityEditor";
import { calculatePrice } from "./calculatePrice";
import { createModel, validateModel } from "./validateModel";
import { postAddToCart } from "./postAddToCart";
import { trackAddToCartEvent } from "../../js/analytics/analytics-events";

export const ProductDetailView: React.FunctionComponent<{
  item: Product;
}> = (props) => {
  const [productSelected, setProductSelected] =
    React.useState<ProductCartModel>(createModel(props.item));

  const [quantity, setQuantity] = React.useState(1);
  const [enhanced, setEnhanced] = React.useState(false);
  const [isAddingToCart, setIsAddingToCart] = React.useState(false);
  const isAddingToCartRef = React.useRef(false);

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isValidated, setIsValidated] = React.useState(false);
  const [revalidateOnChange, setRevalidateOnChange] = React.useState(false);

  const settings = useHgsSettings();

  const validate = React.useCallback(() => {
    const errs = validateModel(props.item, productSelected);
    setErrors(errs);
    setIsValidated(true);
    setRevalidateOnChange(true);
    return Object.keys(errs).length === 0;
  }, [props.item, productSelected]);

  const setAttribute = (option: AttributeOption) => {
    const selectedAttributes = [...productSelected.Attributes];
    const existingAttribute = selectedAttributes.find(
      (attribute) => attribute.AttributeId === option.AttributeId
    );

    if (existingAttribute) {
      existingAttribute.DetailId = option.Id;
      existingAttribute.Name = option.Name;
    } else {
      selectedAttributes.push({
        DetailId: option.Id,
        Name: option.Name,
        AttributeId: option.AttributeId,
      });
    }

    //if (option.Price > 0) {
    const allOptions = props.item.AttributeOptions.flatMap(
      (attr) => attr.Options
    );

    const attributesFull = selectedAttributes
      .map((sel) => allOptions.find((opt) => opt.Id === sel.DetailId))
      .filter((opt): opt is AttributeOption => opt !== undefined);

    productSelected.Price = calculatePrice(attributesFull, props.item.Price);
    //}

    setProductSelected({
      ...productSelected,
      Attributes: selectedAttributes,
    });
  };

  const onClickHandler = async () => {
    if (isAddingToCartRef.current || !validate()) {
      return;
    }

    isAddingToCartRef.current = true;
    setIsAddingToCart(true);

    try {
      const errorMessage = await postAddToCart({
        ...productSelected,
        Quantity: quantity,
      });
      if (errorMessage) {
        isAddingToCartRef.current = false;
        setIsAddingToCart(false);
        setErrors({ submit: "Unable to Add to Cart: " + errorMessage });
        return;
      }
    } catch {
      isAddingToCartRef.current = false;
      setIsAddingToCart(false);
      setErrors({ submit: "Unable to Add to Cart." });
      return;
    }

    try {
      trackAddToCartEvent({
        item_id: productSelected.ProductCode,
        item_name: productSelected.Name,
        price: productSelected.Price,
        quantity,
        description: "",
        image: props.item.Images[0]?.Url ?? "",
      });
    } catch (error) {
      console.error("Error tracking add_to_cart", error);
    }

    document.location.href = "/ShoppingCart.aspx";
  };

  const productContext: ProductContext = {
    product: props.item,
    setAttribute: setAttribute,
    errors,
    isValidated,
    validate,
    setErrors,
    revalidateOnChange,
    setRevalidateOnChange,
  };

  let displayPrice: string | JSX.Element = currencyFormatter.format(
    productSelected.Price
  );
  if (productSelected.Price !== productSelected.OriginalPrice) {
    displayPrice = (
      <>
        <span className="original-price">
          {currencyFormatter.format(productSelected.OriginalPrice)}
        </span>
        {currencyFormatter.format(productSelected.Price)}
      </>
    );
  }
  const nonSalePrice = currencyFormatter.format(productSelected.OriginalPrice);

  React.useEffect(() => {
    // This runs AFTER hydration
    setEnhanced(true);
  }, []);

  React.useEffect(() => {
    const resetAfterHistoryRestore = (event: PageTransitionEvent) => {
      if (event.persisted) {
        isAddingToCartRef.current = false;
        setIsAddingToCart(false);
      }
    };

    window.addEventListener("pageshow", resetAfterHistoryRestore);
    return () => window.removeEventListener("pageshow", resetAfterHistoryRestore);
  }, []);

  React.useEffect(() => {
    if (revalidateOnChange) {
      validate();
    }
  }, [productSelected, validate, revalidateOnChange]);

  const isActive = props.item.IsActive;
  const hasValidationErrors = Object.keys(errors).some(
    (key) => key !== "submit"
  );

  return (
    <ProductContextProvider value={productContext}>
      <div className="price-container">
        {!enhanced && <span className="price">{nonSalePrice}</span>}
        {enhanced && (
          <>
            <span className="price">{displayPrice}</span>
            {settings?.vendorInfo?.ShouldIncludeHandling && (
              <span className="loris-app-only fee-message">
                + 10% processing fee
              </span>
            )}
          </>
        )}
      </div>

      {enhanced && isActive && (
        <div id="pnlCartControls" className="cart-controls">
          <QuantityEditor
            quantity={quantity}
            setQuantity={setQuantity}
          ></QuantityEditor>
        </div>
      )}

      {enhanced && isActive && (
        <div
          className={`product-attributes ${
            productContext.isValidated ? "validated" : ""
          }`}
        >
          <AttributeSelector
            attributes={productContext.product.AttributeOptions}
          ></AttributeSelector>
        </div>
      )}

      {enhanced && isActive && (
        <div className="addToCart">
          {(!isValidated || !hasValidationErrors) && (
            <AddToCartButton
              item={props.item}
              isAddingToCart={isAddingToCart}
              onProductAddToCartClick={onClickHandler}
            ></AddToCartButton>
          )}
          {isValidated && hasValidationErrors && (
            <div
              className="prod-button disabled"
              title="Please review the options before adding this product to the cart"
            >
              Review Options
            </div>
          )}
        </div>
      )}
      {enhanced && !isActive && (
        <div style={{ gridArea: "error" }}>
          <h3>We are sorry, this product is no longer in stock.</h3>
        </div>
      )}
      {errors["submit"] && <span className="error">{errors["submit"]}</span>}
    </ProductContextProvider>
  );
};
